'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/use-toast';
import { useCopy } from '@/app/hooks';
import { reportEvent } from '@/app/utils';
import { getLivePlaybackShareToken } from '../apis';

interface ShareLivePlaybackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 直播回放 id，点击复制时先据此换取分享 token。 */
  playbackId: string;
  /** 所属直播 id，存在时附加到分享链接的 live_id 参数。 */
  liveId?: string;
  description?: string;
  getShareUrl?: () => Promise<string | null>;
  onCopied?: () => void;
}

export const ShareLivePlaybackDialog = ({
  open,
  onOpenChange,
  playbackId,
  liveId,
  description,
  getShareUrl,
  onCopied,
}: ShareLivePlaybackDialogProps) => {
  const t = useTranslations();
  const { copyAsync } = useCopy();
  const [loading, setLoading] = useState(false);

  const handleCopy = async () => {
    const id = playbackId.trim();
    if (!id || loading) return;
    setLoading(true);
    try {
      const shareUrlPromise = getShareUrl
        ? getShareUrl()
        : getLivePlaybackShareToken(id).then(({ data, error }) => {
            const token = data?.token?.trim();
            if (error || !token) {
              toast({ type: 'error', message: t('liveRecords.share.tokenFailed') });
              return null;
            }
            const params = new URLSearchParams({ token });
            const trimmedLiveId = liveId?.trim();
            if (trimmedLiveId) params.set('live_id', trimmedLiveId);
            return `${window.location.origin}/vidu-stream/share-record/${id}?${params.toString()}`;
          });
      await copyAsync(shareUrlPromise, { copySuccessMessage: t('liveRecords.share.linkCopied') });
      if (onCopied) onCopied();
      else reportEvent('stream_element_click', { element_name: 'copy_record_link_click' });
      onOpenChange(false);
    } catch {
      toast({ type: 'error', message: t('liveRecords.share.tokenFailed') });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex w-[min(480px,calc(100%-32px))] max-w-none flex-col gap-0 border-system-white08 bg-[#1c1c1c] p-0 text-white sm:gap-0 md:p-0"
        onClose={() => onOpenChange(false)}
      >
        <DialogHeader className="shrink-0 gap-0 space-y-0 px-6 pt-6 pb-3 sm:px-4 sm:pt-4">
          <DialogTitle className="text-left font-medium text-white text-xl leading-tight">
            {t('liveRecords.share.title')}
          </DialogTitle>
        </DialogHeader>
        <p className="whitespace-pre-line px-6 pb-6 text-sm text-white/60 leading-relaxed sm:px-4 sm:pb-4">
          {description ?? t('liveRecords.share.description')}
        </p>
        <div className="flex shrink-0 justify-end px-6 pb-6 sm:px-4 sm:pb-4">
          <Button variant="primary" size="dialog" className="min-w-[120px]" disabled={loading} onClick={handleCopy}>
            {t('liveRecords.share.copyLink')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
